<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue';
import Calendar from 'primevue/calendar';
import AddOrganizationDocumentModal from '@/components/UploadModal/AddOrganizationDocumentModal.vue';
import OrganizationGeneralDocuments from './components/OrganizationGeneralDocuments.vue';
import OrganizationDataExportDocuments from './components/OrganizationDataExportDocuments.vue';
import AppTabMenu from '@/components/AppTabMenu';
import { useOrganizationDocumentStore, useBillingStore, useOrganizationStore } from '@/store';
import { can, useDateTime, useRouting } from '@/composables';
import { FilterEventInterface, OrganizationDocumentsFilterInterface } from '@/types';
import { PERMISSIONS } from '@/enum';

const defaultFilters: OrganizationDocumentsFilterInterface = {
  search: '',
  page: 0,
  start_at: '',
  end_at: '',
  per_page: 15,
};

const $routing = useRouting();
const organizationStore = useOrganizationStore();
const organizationDocumentStore = useOrganizationDocumentStore();
const billingStore = useBillingStore();
const documentModalOpen = ref(false);
const endDate = ref<Date>();
const filters = ref(defaultFilters);
const firstValue = ref(0);
const startDate = ref<Date>();
const active = ref(0);

const items = [
  {
    label: 'Billing Documents',
    visible: can([PERMISSIONS.ORGANIZATION_BILLING_DOCUMENTS_INDEX]),
    index: 0,
  },
  {
    label: 'Other Documents',
    index: 1,
  },
  {
    label: 'Data Export',
    visible:
      can([PERMISSIONS.ORGANIZATION_DOCUMENTS_GENERATE_DATA_EXPORT]) &&
      organizationStore.organization?.parent_id !== null,
    index: 2,
  },
];

/**
 * Check whether there are any filters defined by check for query params
 * within the route object.
 */
const hasFilter = computed<boolean>(() => {
  const { search, page, per_page } = $routing.route.query;
  return !!(search || page || per_page);
});

const isDataExportEnabled = computed<boolean>(
  () => active.value === 2 && organizationStore.organization?.parent_id !== null,
);

const openBillingDocumentUploader = () => (documentModalOpen.value = true);

onMounted(() => init());

const init = async () => {
  active.value = await getIndex();

  setDates(false);
  await defineFilters();

  if (!can([PERMISSIONS.ORGANIZATION_BILLING_DOCUMENTS_INDEX])) {
    active.value = 1;
  }

  if (filters.value.start_at && filters.value.end_at) {
    organizationDocumentStore.getBillingDocuments($routing.route.query);
  }
};

const getIndex = async () => {
  const index =
    $routing.route.query.index && organizationStore.organization?.parent_id !== null
      ? parseInt($routing.route.query.index as string)
      : 0;
  return index < items.length ? index : 0;
};

/**
 * set the default start/end dates.
 * replace them in defineFilters if a URL param
 * set the start date to four months back from today
 *  */
const setDates = (reset: boolean) => {
  const query = $routing.route.query;
  const currentDate = new Date();
  firstValue.value = 0;
  const startParam =
    query.start_at && !reset
      ? new Date(query.start_at as string)
      : new Date(currentDate.setDate(currentDate.getDate() - 84));
  const endParam = query.end_at && !reset ? new Date(query.end_at as string) : new Date();

  startParam.setDate(startParam.getDate() + 1);
  endParam.setDate(endParam.getDate() + 1);
  startDate.value = startParam;
  endDate.value = endParam;

  $routing.resetQueryParams({
    start_at: query.start_at || useDateTime().formatDateForApi(startParam),
    end_at: query.end_at || useDateTime().formatDateForApi(endParam),
    index: active.value || 0,
  });
};

/**
 * Define the filters based on the query params available on page load.
 */
const defineFilters = async () => {
  const query = $routing.route.query;

  filters.value = {
    search: query.search ? (query.search as string) : '',
    start_at: query.start_at ? (query.start_at as string) : '',
    end_at: query.end_at ? (query.end_at as string) : '',
    page: query.page ? parseInt(query.page as string) : 0,
    per_page: query.per_page ? parseInt(query.per_page as string) : defaultFilters.per_page,
  };

  if ($routing.route.query.page || $routing.route.query.per_page) {
    firstValue.value = (filters.value.page - 1) * filters.value.per_page;
  } else {
    firstValue.value = 0;
  }
};

const doToast = (summary: string, detail: string) => {
  organizationDocumentStore.doToast(detail, summary);
};

const doFailUnauthorized = (action: string) => {
  organizationDocumentStore.doToast(`User unauthorized to ${action} documents.`, 'Error');
};

/**
 * Watch for changes to the route object in order to execute a
 * new request for data.
 */
$routing.routeChangeWatcher(async () => {
  if (!can([PERMISSIONS.ORGANIZATION_BILLING_DOCUMENTS_INDEX]) || active.value !== 0) return;
  await organizationDocumentStore.getBillingDocuments($routing.route.query);
});

const onStartDateRangeChange = async (dateStart: Date) => {
  const start = useDateTime().formatDateForApi(dateStart);
  $routing.addQueryParams({ start_at: start, end_at: useDateTime().formatDateForApi(endDate.value) });
};

const onEndDateRangeChange = async (dateEnd: Date) => {
  const end = useDateTime().formatDateForApi(dateEnd);
  $routing.addQueryParams({ start_at: useDateTime().formatDateForApi(startDate.value), end_at: end });
};

const onPage = (event: FilterEventInterface) => {
  filters.value.page = event.page;
  filters.value.per_page = event.rows ? event.rows : 15;
  $routing.addQueryParams({ page: event.page + 1, per_page: event.rows });
};

const onSaveAdd = () => {
  if (can([PERMISSIONS.ORGANIZATION_BILLING_DOCUMENTS_STORE])) {
    organizationDocumentStore.getBillingDocuments($routing.route.query);
    doToast('Added', 'New Document has been added');
  } else {
    doFailUnauthorized('upload');
  }
};

const onCancelAdd = () => (documentModalOpen.value = false);

/**
 * Handle the ordering of the request data.
 */
const onSort = (event: FilterEventInterface) => {
  const order = event.sortOrder === 1 ? 'asc' : 'desc';
  $routing.addQueryParams({ order, order_by: event.sortField });
};

/**
 * Handle the filtering of results based on the provided search terms,
 * given 3 or or more characters have been provided.
 */
const onSearch = (search: string) => {
  const hasSearchTerm = search.length >= 3;

  if (hasSearchTerm) {
    $routing.addQueryParam('search', search);
    filters.value.search = search;
  } else {
    $routing.removeQueryParam('search');
  }
};

/**
 * clear all filters
 */
const onClearFilters = async () => {
  filters.value = { ...defaultFilters };
  firstValue.value = 0;
  setDates(true);
};

const datesInverted = computed(() => {
  return startDate.value && endDate.value ? startDate.value > endDate.value : false;
});
/**
 * download the invoice.
 * split the name to get the extension and if no extension use default xlsx
 * should handle pdf, zip and xlsx files
 * @param id
 * @param name
 */
const onDownloadInvoice = async (id: number, name: string) => {
  if (can([PERMISSIONS.ORGANIZATION_BILLING_DOCUMENTS_DOWNLOAD])) {
    const nameAr = name.split('.');
    const extension = nameAr[1] ? nameAr[1] : 'xlsx';
    await billingStore.downloadInvoice(id, nameAr[0], extension);
    doToast('Downloaded', 'Document successfully downloaded');
  } else {
    doFailUnauthorized('download');
  }
};

const onDeleteDocument: (id: number) => Promise<void> = async (id) => {
  if (can([PERMISSIONS.ORGANIZATION_BILLING_DOCUMENTS_DESTROY])) {
    await organizationDocumentStore.deleteBillingDocument(id);
    await organizationDocumentStore.getBillingDocuments($routing.route.query);
    doToast('Removed', 'Document has been removed');
  } else {
    doFailUnauthorized('delete');
  }
};

const handleTabMenuSelect = (index: number) => {
  active.value = index;
  $routing.addQueryParam('index', index.toString());
};
</script>

<template>
  <div class="flex-row w-full">
    <div class="page-header-tab-bar pb-4">
      <AppTabMenu :items="items" :active-index="active" @tab="handleTabMenuSelect" />
    </div>
    <Card v-if="active === 0">
      <template #header>
        <div class="flex gap-3 p-3">
          <div class="flex flex-wrap gap-3 flex-1 document-header">
            <div>
              <IconField icon-position="left">
                <InputIcon>
                  <Icon name="search" class="text-lg" />
                </InputIcon>
                <InputText
                  v-model="filters.search"
                  v-debounce:600ms="onSearch"
                  class="w-full text-sm"
                  placeholder="Search documents"
                />
              </IconField>
            </div>
            <div class="flex flex-row gap-1 align-items-center justify-content-start font-bold text-sm">
              <Calendar
                v-model="startDate"
                :show-icon="true"
                :manual-input="false"
                icon-display="input"
                view="month"
                class="w-full text-sm"
                date-format="MM yy"
                @date-select="onStartDateRangeChange"
              />
              to
              <Calendar
                v-model="endDate"
                view="month"
                :show-icon="true"
                :manual-input="false"
                icon-display="input"
                class="w-full text-sm"
                date-format="MM yy"
                @date-select="onEndDateRangeChange"
              />
            </div>
            <AppButton
              v-if="hasFilter"
              type="secondary"
              text="Clear Filters"
              icon-left="clear"
              @click="onClearFilters"
            />
            <div v-if="datesInverted" class="flex input-errors align-items-center h-full font-bold">
              <small>End date must be after beginning date</small>
            </div>
          </div>
        </div>
        <div class="ml-auto">
          <AppButton
            v-if="can([PERMISSIONS.ORGANIZATION_BILLING_DOCUMENTS_STORE])"
            type="primary"
            text="Upload Document"
            icon-left="cloud-upload"
            @click="openBillingDocumentUploader"
          />
        </div>
      </template>
      <template #content>
        <DataTable
          v-model:filters="filters"
          filter-display="menu"
          :value="organizationDocumentStore.billingDocuments.data"
          :paginator="true"
          data-key="id"
          row-group-mode="subheader"
          group-rows-by="invoice_id"
          sort-field="month_year"
          :rows="filters.per_page"
          :total-records="organizationDocumentStore.billingDocuments.total"
          :row-hover="true"
          :lazy="true"
          loading-icon="logo-spinner"
          :loading="organizationDocumentStore.loading"
          :rows-per-page-options="[15, 50, 100]"
          paginator-template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          current-page-report-template="Showing {first} to {last} of {totalRecords}"
          responsive-layout="scroll"
          :first="firstValue"
          sort-mode="single"
          scrollable
          @page="onPage"
          @sort="onSort"
        >
          <template #empty>No documents found</template>
          <template v-if="organizationDocumentStore.billingDocuments">
            <Column field="name" header="Title">
              <template #body="slotProps">
                <span
                  v-if="can([PERMISSIONS.ORGANIZATION_BILLING_DOCUMENTS_DOWNLOAD])"
                  class="font-semibold cursor-pointer download"
                  @click="onDownloadInvoice(slotProps.data.id, slotProps.data.name)"
                  v-text="slotProps.data.name"
                />
                <span v-else class="font-semibold" v-text="slotProps.data.name" />
              </template>
            </Column>
            <Column field="created_at" header="Date Created" class="justify-items-center">
              <template #body="slotProps">
                <span class="date-created" v-text="useDateTime().formatIntlDate(slotProps.data.created_at)" />
              </template>
            </Column>
            <Column
              body-class="justify-content-center"
              body-style="font-size: .8725rem;"
              header-class="justify-content-center"
              header="Actions"
            >
              <template #body="{ data }">
                <AppButton
                  v-if="can([PERMISSIONS.ORGANIZATION_BILLING_DOCUMENTS_DOWNLOAD])"
                  type="text"
                  size="large"
                  icon-left="cloud_download"
                  class="text-600 cloud-download"
                  @click="onDownloadInvoice(data.id, data.name)"
                />
                <AppButton
                  v-if="
                    can([PERMISSIONS.ORGANIZATION_BILLING_DOCUMENTS_DESTROY]) &&
                    data.description == 'Manually uploaded document'
                  "
                  type="text"
                  size="large"
                  icon-left="delete"
                  class="text-600"
                  @click="onDeleteDocument(data.id)"
                />
              </template>
            </Column>
          </template>
          <template #groupheader="slotProps">
            <span v-text="slotProps.data.month_year" />
          </template>
        </DataTable>
        <AddOrganizationDocumentModal
          v-if="documentModalOpen && can([PERMISSIONS.ORGANIZATION_BILLING_DOCUMENTS_STORE])"
          title="Upload Document"
          :billing="true"
          @cancel="onCancelAdd"
          @save="onSaveAdd"
        />
      </template>
    </Card>
    <OrganizationGeneralDocuments v-if="active === 1" />
    <OrganizationDataExportDocuments v-if="isDataExportEnabled" />
  </div>
</template>

<style lang="scss" scoped>
.input-errors {
  color: $errorColor;
}
:deep(.p-card-header) {
  padding: 0 1rem !important;
}
:deep(.p-card-body) {
  padding: 0;
  .p-datatable {
    margin: 0 !important;
  }

  .p-datatable-header {
    background: $white;
    padding: 0.5rem;
  }
  .p-datatable-thead {
    .p-column-header-content {
      color: $darkMedium;
      font-weight: bold;
      font-size: 14px;
    }
  }

  .p-rowgroup-header,
  .p-rowgroup-header:hover {
    background: var(--primary-color-extra-light) !important;
    padding: 3px 12px;
    height: 24px;
    align-items: center;
    color: $darkMedium !important;
    font-style: normal;
    font-weight: bold;
    font-size: 14px;
    line-height: 18px;

    td {
      padding-left: 0;
      border-width: 0;
    }
  }

  .p-datatable-wrapper {
    margin: 0 !important;
    table-layout: fixed;
  }

  .p-paginator-current {
    flex: 1;
    justify-content: flex-end;
  }

  .p-datatable-row-expansion {
    td {
      &:first-child {
        padding: 0;
      }
    }

    .p-datatable-thead {
      visibility: collapse;
    }
  }
}

:deep(.p-calendar) {
  .p-inputtext {
    font-size: 14px;
  }
}
.date-created {
  font-size: 14px;
  line-height: 18px;
  color: #3b4242;
}

.download {
  color: var(--primary-700);
}

.document-header {
  color: $darkMedium;
}
</style>
